import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';

// Define the structure for consent settings
interface ConsentSettings {
  analytics: boolean;
  advertising: boolean;
  functional: boolean;
  necessary: boolean;
}

// Define cookie preferences
interface CookiePreferences {
  acceptAll: boolean;
  rejectAll: boolean;
  customized: boolean;
  expiryDays: number;
  categories: {
    essential: boolean;      // Required cookies that enable core functionality
    functional: boolean;     // Cookies for enhanced functionality and preferences
    analytics: boolean;      // Cookies that help us understand how our website is used
    advertising: boolean;    // Cookies used to provide targeted advertising
    social: boolean;        // Cookies set by social media services
  };
  vendors: {
    google: boolean;
    facebook: boolean;
    microsoft: boolean;
    stripe: boolean;
    hubspot: boolean;
  };
}

// Define the structure for integration consent
interface IntegrationConsent {
  dataAccess: boolean;
  dataSharing: boolean;
  emailAccess: boolean;
  calendarAccess: boolean;
  fileAccess: boolean;
}

// Define loading states for different sections
interface LoadingStates {
  cookiePreferences: boolean;
  privacyPolicy: boolean;
  termsOfService: boolean;
  integrations: {
    microsoft: boolean;
    google: boolean;
    facebook: boolean;
  };
  marketingPreferences: boolean;
}

// Define the structure for the privacy state
interface PrivacyState {
  cookieConsent: ConsentSettings;
  cookiePreferences: CookiePreferences;
  microsoftIntegration: IntegrationConsent;
  googleIntegration: IntegrationConsent;
  facebookIntegration: IntegrationConsent;
  consentVersion: string;
  lastUpdated: string;
  hasAcceptedTerms: boolean;
  hasAcceptedPrivacyPolicy: boolean;
  privacyPolicyVersion: string;
  termsVersion: string;
  marketingPreferences: {
    email: boolean;
    sms: boolean;
    push: boolean;
    inApp: boolean;
  };
  loading: LoadingStates;
  error: Partial<Record<keyof LoadingStates, string | null>>;
}

export interface GlobalPrivacyState {
  privacy: PrivacyState
}

// Initial state
const initialState: PrivacyState = {
  // Implied consent with an opt-out, the common practice for an Alberta
  // business serving Canadian visitors, and what the WordPress site this app
  // replaces already does (it grants everything and shows no banner).
  // Analytics and advertising start granted so GA4 keeps its cookies, the Ads
  // tags can read the linker cookie, and enhanced conversions have an
  // ad_user_data grant to work under. A stored choice still wins: the consent
  // cookie is read on mount and dispatched over these defaults.
  cookieConsent: {
    analytics: true,
    advertising: true,
    functional: false,
    necessary: true, // Necessary cookies are always allowed
  },
  cookiePreferences: {
    acceptAll: false,
    rejectAll: false,
    customized: false,
    expiryDays: 365,
    categories: {
      essential: true,      // Always true as these are required
      functional: false,
      analytics: false,
      advertising: false,
      social: false,
    },
    vendors: {
      google: false,
      facebook: false,
      microsoft: false,
      stripe: false,
      hubspot: false,
    },
  },
  microsoftIntegration: {
    dataAccess: false,
    dataSharing: false,
    emailAccess: false,
    calendarAccess: false,
    fileAccess: false,
  },
  googleIntegration: {
    dataAccess: false,
    dataSharing: false,
    emailAccess: false,
    calendarAccess: false,
    fileAccess: false,
  },
  facebookIntegration: {
    dataAccess: false,
    dataSharing: false,
    emailAccess: false,
    calendarAccess: false,
    fileAccess: false,
  },
  consentVersion: '1.0',
  lastUpdated: new Date().toISOString(),
  hasAcceptedTerms: false,
  hasAcceptedPrivacyPolicy: false,
  privacyPolicyVersion: '1.0',
  termsVersion: '1.0',
  marketingPreferences: {
    email: false,
    sms: false,
    push: false,
    inApp: true,
  },
  loading: {
    cookiePreferences: false,
    privacyPolicy: false,
    termsOfService: false,
    integrations: {
      microsoft: false,
      google: false,
      facebook: false,
    },
    marketingPreferences: false,
  },
  error: {},
};

// Create the slice
const globalPrivacySlice = createSlice({
  name: 'privacy',
  initialState,
  reducers: {
    setCookieConsent: (state, action: PayloadAction<Partial<ConsentSettings>>) => {
      state.cookieConsent = { ...state.cookieConsent, ...action.payload };
      state.lastUpdated = new Date().toISOString();
    },
    updateCookiePreferences: (state, action: PayloadAction<Partial<CookiePreferences>>) => {
      state.cookiePreferences = { ...state.cookiePreferences, ...action.payload };
      state.lastUpdated = new Date().toISOString();
    },
    acceptAllCookies: (state) => {
      state.cookiePreferences = {
        ...state.cookiePreferences,
        acceptAll: true,
        rejectAll: false,
        customized: false,
        categories: {
          essential: true,
          functional: true,
          analytics: true,
          advertising: true,
          social: true,
        },
        vendors: {
          google: true,
          facebook: true,
          microsoft: true,
          stripe: true,
          hubspot: true,
        },
      };
      state.lastUpdated = new Date().toISOString();
    },
    rejectAllCookies: (state) => {
      state.cookiePreferences = {
        ...state.cookiePreferences,
        acceptAll: false,
        rejectAll: true,
        customized: false,
        categories: {
          essential: true, // Essential remains true
          functional: false,
          analytics: false,
          advertising: false,
          social: false,
        },
        vendors: {
          google: false,
          facebook: false,
          microsoft: false,
          stripe: false,
          hubspot: false,
        },
      };
      state.lastUpdated = new Date().toISOString();
    },
    setFacebookIntegrationConsent: (state, action: PayloadAction<Partial<IntegrationConsent>>) => {
      state.facebookIntegration = { ...state.facebookIntegration, ...action.payload };
      state.lastUpdated = new Date().toISOString();
    },
    setMicrosoftIntegrationConsent: (state, action: PayloadAction<Partial<IntegrationConsent>>) => {
      state.microsoftIntegration = { ...state.microsoftIntegration, ...action.payload };
      state.lastUpdated = new Date().toISOString();
    },
    setGoogleIntegrationConsent: (state, action: PayloadAction<Partial<IntegrationConsent>>) => {
      state.googleIntegration = { ...state.googleIntegration, ...action.payload };
      state.lastUpdated = new Date().toISOString();
    },
    updateConsentVersion: (state, action: PayloadAction<string>) => {
      state.consentVersion = action.payload;
      state.lastUpdated = new Date().toISOString();
    },
    acceptTerms: (state, action: PayloadAction<string>) => {
      state.hasAcceptedTerms = true;
      state.termsVersion = action.payload;
      state.lastUpdated = new Date().toISOString();
    },
    acceptPrivacyPolicy: (state, action: PayloadAction<string>) => {
      state.hasAcceptedPrivacyPolicy = true;
      state.privacyPolicyVersion = action.payload;
      state.lastUpdated = new Date().toISOString();
    },
    updateMarketingPreferences: (state, action: PayloadAction<Partial<PrivacyState['marketingPreferences']>>) => {
      state.marketingPreferences = { ...state.marketingPreferences, ...action.payload };
      state.lastUpdated = new Date().toISOString();
    },
    resetAllConsent: (state) => {
      return { ...initialState, lastUpdated: new Date().toISOString() };
    },
    setLoading: (state, action: PayloadAction<{ key: keyof LoadingStates | keyof LoadingStates['integrations']; isLoading: boolean }>) => {
      const { key, isLoading } = action.payload;

      // Initialize loading state if undefined
      if (!state.loading) {
        state.loading = {
          cookiePreferences: false,
          privacyPolicy: false,
          termsOfService: false,
          integrations: {
            microsoft: false,
            google: false,
            facebook: false,
          },
          marketingPreferences: false,
        };
      }

      // Handle nested loading states for integrations
      if (key === 'microsoft' || key === 'google' || key === 'facebook') {
        state.loading.integrations[key] = isLoading;
      } else {
        state.loading[key as keyof Omit<LoadingStates, 'integrations'>] = isLoading;
      }

      // Clear error when starting to load
      if (isLoading && state.error && key in state.error) {
        state.error[key as keyof typeof state.error] = null;
      }
    },
    setIntegrationLoading: (
      state,
      action: PayloadAction<{ integration: keyof LoadingStates['integrations']; isLoading: boolean }>
    ) => {
      const { integration, isLoading } = action.payload;
      state.loading.integrations[integration] = isLoading;
      // Clear error when starting to load
      if (isLoading) {
        delete state.error.integrations;
      }
    },
    setError: (state, action: PayloadAction<{ key: keyof LoadingStates; error: string }>) => {
      const { key, error } = action.payload;
      state.error[key] = error;
      // Clear loading state when error occurs
      if (key === 'integrations') {
        state.loading.integrations = {
          microsoft: false,
          google: false,
          facebook: false,
        };
      } else {
        state.loading[key] = false;
      }
    },
  },
});

// Export actions
export const {
  setCookieConsent,
  updateCookiePreferences,
  acceptAllCookies,
  rejectAllCookies,
  setMicrosoftIntegrationConsent,
  setGoogleIntegrationConsent,
  setFacebookIntegrationConsent,
  updateConsentVersion,
  acceptTerms,
  acceptPrivacyPolicy,
  updateMarketingPreferences,
  resetAllConsent,
  setLoading,
  setIntegrationLoading,
  setError,
} = globalPrivacySlice.actions;

// Export reducer
export default globalPrivacySlice.reducer;

// Helper function to check if state is initialized
const isInitializedState = (state: unknown): state is { privacy: PrivacyState } => {
  return state !== null &&
         typeof state === 'object' &&
         'privacy' in state &&
         state.privacy !== null &&
         typeof state.privacy === 'object';
};

// Default values
const defaultCookieConsent: ConsentSettings = {
  analytics: false,
  advertising: false,
  functional: false,
  necessary: true,
};

const defaultCookiePreferences: CookiePreferences = {
  acceptAll: false,
  rejectAll: false,
  customized: false,
  expiryDays: 365,
  categories: {
    essential: true,
    functional: false,
    analytics: false,
    advertising: false,
    social: false,
  },
  vendors: {
    google: false,
    facebook: false,
    microsoft: false,
    stripe: false,
    hubspot: false,
  },
};

const defaultIntegrationConsent: IntegrationConsent = {
  dataAccess: false,
  dataSharing: false,
  emailAccess: false,
  calendarAccess: false,
  fileAccess: false,
};

// Base selectors
const selectPrivacyState = (state: unknown) => {
  if (!isInitializedState(state)) return null;
  return state.privacy;
};

// Memoized selectors with loading states
export const selectCookiePreferencesWithLoading = createSelector(
  [selectPrivacyState],
  (privacyState): SelectionWithLoading<CookiePreferences> => {
    if (!privacyState) {
      return {
        data: defaultCookiePreferences,
        isLoading: false,
        error: null,
      };
    }
    return {
      data: privacyState.cookiePreferences ?? defaultCookiePreferences,
      isLoading: privacyState.loading?.cookiePreferences ?? false,
      error: privacyState.error?.cookiePreferences ?? null,
    };
  }
);

export const selectPrivacyPolicyAcceptanceWithLoading = createSelector(
  [selectPrivacyState],
  (privacyState): SelectionWithLoading<{ hasAccepted: boolean; version: string }> => {
    if (!privacyState) {
      return {
        data: { hasAccepted: false, version: '1.0' },
        isLoading: false,
        error: null,
      };
    }
    return {
      data: {
        hasAccepted: privacyState.hasAcceptedPrivacyPolicy ?? false,
        version: privacyState.privacyPolicyVersion ?? '1.0',
      },
      isLoading: privacyState.loading?.privacyPolicy ?? false,
      error: privacyState.error?.privacyPolicy ?? null,
    };
  }
);

export const selectTermsAcceptanceWithLoading = createSelector(
  [selectPrivacyState],
  (privacyState): SelectionWithLoading<{ hasAccepted: boolean; version: string }> => {
    if (!privacyState) {
      return {
        data: { hasAccepted: false, version: '1.0' },
        isLoading: false,
        error: null,
      };
    }
    return {
      data: {
        hasAccepted: privacyState.hasAcceptedTerms ?? false,
        version: privacyState.termsVersion ?? '1.0',
      },
      isLoading: privacyState.loading?.termsOfService ?? false,
      error: privacyState.error?.termsOfService ?? null,
    };
  }
);

// Other memoized selectors
export const selectCookieConsent = createSelector(
  [selectPrivacyState],
  (privacyState): ConsentSettings => {
    if (!privacyState) return defaultCookieConsent;
    return privacyState.cookieConsent ?? defaultCookieConsent;
  }
);

export const selectCookiePreferences = createSelector(
  [selectPrivacyState],
  (privacyState): CookiePreferences => {
    if (!privacyState) return defaultCookiePreferences;
    return privacyState.cookiePreferences ?? defaultCookiePreferences;
  }
);

export const selectMicrosoftIntegrationConsent = createSelector(
  [selectPrivacyState],
  (privacyState): IntegrationConsent => {
    if (!privacyState) return defaultIntegrationConsent;
    return privacyState.microsoftIntegration ?? defaultIntegrationConsent;
  }
);

export const selectGoogleIntegrationConsent = createSelector(
  [selectPrivacyState],
  (privacyState): IntegrationConsent => {
    if (!privacyState) return defaultIntegrationConsent;
    return privacyState.googleIntegration ?? defaultIntegrationConsent;
  }
);

export const selectConsentVersion = createSelector(
  [selectPrivacyState],
  (privacyState): string => {
    if (!privacyState) return '1.0';
    return privacyState.consentVersion ?? '1.0';
  }
);

export const selectLastUpdated = createSelector(
  [selectPrivacyState],
  (privacyState): string => {
    if (!privacyState) return new Date().toISOString();
    return privacyState.lastUpdated ?? new Date().toISOString();
  }
);

export const selectMarketingPreferences = createSelector(
  [selectPrivacyState],
  (privacyState): PrivacyState['marketingPreferences'] => {
    if (!privacyState) return initialState.marketingPreferences;
    return privacyState.marketingPreferences ?? initialState.marketingPreferences;
  }
);

export const selectLoadingStates = createSelector(
  [selectPrivacyState],
  (privacyState): LoadingStates => {
    if (!privacyState) return initialState.loading;
    return privacyState.loading;
  }
);

export const selectErrors = createSelector(
  [selectPrivacyState],
  (privacyState): PrivacyState['error'] => {
    if (!privacyState) return {};
    return privacyState.error;
  }
);

// Update existing selectors to include loading state information
interface SelectionWithLoading<T> {
  data: T;
  isLoading: boolean;
  error: string | null;
}

